import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useTabParam } from "@/lib/useTabParam";
import { toast } from "sonner";
import { BookOpen, CheckCircle2, XCircle, Archive, BarChart3, MessageSquareWarning } from "lucide-react";

/**
 * MERCHANT-SUPPORT-KNOWLEDGE-1 Phase A · Admin knowledge review.
 * Four tabs: Candidates · Approved · Unanswered · Metrics.
 * Zero-LLM everywhere — this UI never triggers a generation call.
 */
export default function AdminSupportKnowledge() {
  const [tab, setTab] = useTabParam("candidates");
  return (
    <div className="p-6" data-testid="admin-support-knowledge">
      <div className="flex items-center gap-2 mb-1">
        <BookOpen className="w-5 h-5 text-burgundy-600" />
        <h1 className="font-display text-2xl tracking-tight text-slate-900">Support knowledge</h1>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Deterministic merchant support knowledge (Phase A). No LLM tokens are ever spent from this surface.
      </p>

      <div className="flex gap-2 border-b border-slate-200 mb-4 overflow-x-auto">
        {[
          ["candidates", "Candidates"],
          ["approved", "Approved"],
          ["unanswered", "Unanswered"],
          ["metrics", "Metrics"],
        ].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            data-testid={`support-knowledge-tab-${id}`}
            className={`px-3 py-2 text-sm font-medium border-b-2 ${
              tab === id ? "border-burgundy-600 text-burgundy-700" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "candidates" && <CandidatesTab />}
      {tab === "approved" && <ApprovedTab />}
      {tab === "unanswered" && <UnansweredTab />}
      {tab === "metrics" && <MetricsTab />}
    </div>
  );
}


function CandidatesTab() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/support/knowledge/candidates")
    .then((r) => setRows(r.data.candidates || []));
  useEffect(() => { load(); }, []);

  return (
    <div className="pf-card" data-testid="support-candidates-tab">
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">No candidates yet. They accumulate when the same unmatched question appears 3+ times.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((c) => (
            <li key={c.id} className="py-3" data-testid={`candidate-row-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">
                    Seen {c.occurrence_count}× · {c.review_ready ? "review-ready" : "watching"}
                  </div>
                  <p className="text-sm font-medium text-slate-800 mt-1">{c.representative_question}</p>
                  <p className="text-xs text-slate-500 mt-1">Normalized: <span className="font-mono">{c.normalized_question}</span></p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={async () => {
                      const intent = window.prompt("Canonical intent (e.g. PROVINCE_COVERAGE_EXPANSION):", "");
                      if (!intent) return;
                      const answer = window.prompt("Approved answer:", "");
                      if (!answer) return;
                      try {
                        await api.post(`/admin/support/knowledge/candidates/${c.id}/approve`, {
                          canonical_intent: intent.toUpperCase(),
                          canonical_question: c.representative_question,
                          approved_answer: answer,
                          category: "general",
                          source_type: "manual",
                          source_reference: "admin",
                          keywords: [],
                          aliases: [c.normalized_question],
                        });
                        toast.success("Approved");
                        load();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Approve failed");
                      }
                    }}
                    data-testid={`candidate-approve-${c.id}`}
                    className="px-2 py-1 text-xs rounded-md bg-emerald-600 text-white hover:opacity-90"
                  >Approve</button>
                  <button
                    onClick={async () => {
                      await api.post(`/admin/support/knowledge/candidates/${c.id}/reject`);
                      toast.success("Rejected");
                      load();
                    }}
                    data-testid={`candidate-reject-${c.id}`}
                    className="px-2 py-1 text-xs rounded-md border border-slate-200 hover:bg-red-50"
                  >Reject</button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function ApprovedTab() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/support/knowledge/answers?status=APPROVED")
    .then((r) => setRows(r.data.answers || []));
  useEffect(() => { load(); }, []);

  return (
    <div className="pf-card" data-testid="support-approved-tab">
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">No approved answers yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((a) => (
            <li key={a.id} className="py-3" data-testid={`approved-row-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">
                    {a.canonical_intent} · v{a.version} · used {a.usage_count}× · 👍 {a.helpful_count} / 👎 {a.unhelpful_count}
                    {a.review_required && <span className="ml-2 text-amber-600 font-semibold">REVIEW</span>}
                  </div>
                  <p className="text-sm font-medium text-slate-800 mt-1">{a.canonical_question}</p>
                  <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap line-clamp-3">{a.approved_answer}</p>
                </div>
                <button
                  onClick={async () => {
                    if (!window.confirm(`Retire "${a.canonical_intent}"? It will stop serving immediately.`)) return;
                    await api.post(`/admin/support/knowledge/answers/${a.id}/retire`);
                    toast.success("Retired");
                    load();
                  }}
                  data-testid={`approved-retire-${a.id}`}
                  className="px-2 py-1 text-xs rounded-md border border-slate-200 hover:bg-red-50 shrink-0"
                >
                  <Archive className="w-3 h-3 inline mr-1" /> Retire
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function UnansweredTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/support/knowledge/unanswered").then((r) => setRows(r.data.unanswered || []));
  }, []);
  return (
    <div className="pf-card" data-testid="support-unanswered-tab">
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">No unanswered questions right now.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-400">
              <th className="py-1 pr-4">Count</th>
              <th className="py-1 pr-4">Question</th>
              <th className="py-1 pr-4">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} data-testid={`unanswered-row-${i}`} className="border-t border-slate-100">
                <td className="py-2 pr-4 font-mono">{r.count}</td>
                <td className="py-2 pr-4">{r.sample}</td>
                <td className="py-2 pr-4 text-xs text-slate-400">{r.last_seen?.slice(0, 16)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


function MetricsTab() {
  const [m, setM] = useState(null);
  const [top, setTop] = useState([]);
  const [ai, setAi] = useState(null);
  useEffect(() => {
    api.get("/admin/support/knowledge/metrics").then((r) => setM(r.data));
    api.get("/admin/support/knowledge/most-asked").then((r) => setTop(r.data.most_asked || []));
    api.get("/admin/support/knowledge/ai-status").then((r) => setAi(r.data)).catch(() => {});
  }, []);
  if (!m) return <div className="pf-card text-sm text-slate-500">Loading…</div>;

  const cards = [
    ["Total questions", m.total_questions],
    ["Matched (approved)", m.matched_approved],
    ["LLM answered", m.llm_answered ?? 0],
    ["Unmatched", m.unmatched],
    ["Escalated", m.escalated],
    ["Helpful votes", m.helpful],
    ["Unhelpful votes", m.unhelpful],
    ["Approved answers", m.approved_answers],
  ];

  const stateColor = ai?.state === "paused" ? "text-red-600"
    : ai?.state === "warning" ? "text-amber-600"
    : "text-emerald-600";

  return (
    <div className="space-y-4" data-testid="support-metrics-tab">
      {ai && (
        <div className="pf-card" data-testid="support-ai-status-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <MessageSquareWarning className={`w-4 h-4 ${stateColor}`} />
              <h3 className="font-display text-lg tracking-tight">AI fallback status</h3>
            </div>
            <span className={`text-xs font-mono uppercase ${stateColor}`}>{ai.state}</span>
          </div>
          {ai.state === "warning" && (
            <div className="mb-2 text-xs p-2 rounded-md bg-amber-50 text-amber-800" data-testid="ai-warning-banner">
              80% threshold reached — AI still enabled. Monitor spend.
            </div>
          )}
          {ai.state === "paused" && (
            <div className="mb-2 text-xs p-2 rounded-md bg-red-50 text-red-800" data-testid="ai-paused-banner">
              Circuit breaker tripped. New AI calls blocked until UTC rollover.
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Metric label="Model" value={ai.model} mono />
            <Metric label="Kill switch" value={ai.enabled ? "on" : "off"} mono />
            <Metric label="Calls today" value={`${ai.today.calls} / ${ai.limits.calls}`} mono />
            <Metric label="Tokens today" value={`${ai.today.total_tokens.toLocaleString()} / ${ai.limits.tokens.toLocaleString()}`} mono />
            <Metric label="Cost today" value={`US$${ai.today.cost_usd.toFixed(2)} / US$${ai.limits.cost_usd.toFixed(2)}`} mono />
            <Metric label="Cost headroom" value={`${ai.headroom_pct.cost}%`} mono />
            <Metric label="Token headroom" value={`${ai.headroom_pct.tokens}%`} mono />
            <Metric label="Call headroom" value={`${ai.headroom_pct.calls}%`} mono />
          </div>
        </div>
      )}

      <div className="pf-card">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="w-4 h-4 text-cyan-500" />
          <h3 className="font-display text-lg tracking-tight">AI avoidance</h3>
        </div>
        <div className="text-4xl font-mono text-emerald-600" data-testid="metric-ai-avoidance">
          {m.ai_avoidance_rate_pct}%
        </div>
        <p className="text-xs text-slate-500 mt-1">
          AI fallback rate: <span className="font-mono">{m.ai_fallback_rate_pct ?? 0}%</span> ·
          LLM calls: <span className="font-mono">{m.llm_calls}</span> ·
          tokens: {(m.total_tokens ?? 0).toLocaleString()} ·
          AI cost: ${(m.ai_cost ?? 0).toFixed(2)} ·
          per-AI-answer: ${(m.cost_per_ai_answer ?? 0).toFixed(4)}
        </p>
      </div>
      <div className="pf-card">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {cards.map(([label, val]) => (
            <div key={label} className="p-3 rounded-xl bg-white/60">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
              <div className="font-mono text-2xl mt-1">{val}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="pf-card">
        <h3 className="font-display text-lg tracking-tight mb-2">Most asked</h3>
        {top.length === 0 ? (
          <p className="text-sm text-slate-500">No canonical-intent traffic yet.</p>
        ) : (
          <ul className="text-sm">
            {top.map((t) => (
              <li key={t.canonical_intent} className="py-1 flex justify-between border-b border-slate-100">
                <span>{t.canonical_intent}</span>
                <span className="font-mono">{t.count}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


function Metric({ label, value, mono }) {
  return (
    <div className="p-2 rounded-lg bg-white/60">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`${mono ? "font-mono" : ""} text-sm mt-1`}>{value}</div>
    </div>
  );
}
